import { useMutation, useQuery } from 'convex/react';
import type { FunctionReturnType } from 'convex/server';
import { api } from '@aeon-karaoke/cloud-convex';
import type { Id } from '@aeon-karaoke/cloud-convex/_generated/dataModel';
import { useCurrentUser } from './useCurrentUser';

export type FriendList = FunctionReturnType<typeof api.friends.list>;
export type Friend = FriendList['friends'][number];
export type FriendRequest = FriendList['requests'][number];
export type FeedItem = FunctionReturnType<typeof api.friends.feed>[number];
export type PersonMatch = FunctionReturnType<typeof api.friends.search>[number];
export type Suggestion = FunctionReturnType<typeof api.friends.suggestions>[number];
export type BlockedPerson = FunctionReturnType<typeof api.blocks.list>[number];
/** From the model, not a read: every read narrows 'blocked' away, and the Blocked list needs it. */
export type { Relation } from '@aeon-karaoke/cloud-convex/model/friends';

/** Friends with song counts and the requests waiting on the caller. Skips
 * until the users row exists (sign-in bootstrap window). */
export function useFriendList() {
  const { user } = useCurrentUser();
  return useQuery(api.friends.list, user ? {} : 'skip');
}

/** What friends sang lately, newest first. */
export function useFriendFeed(limit: number) {
  const { user } = useCurrentUser();
  return useQuery(api.friends.feed, user ? { limit } : 'skip');
}

/** People who shared a session with the caller and are not friends yet, each
 * with the room and night as the reason. */
export function useFriendSuggestions() {
  const { user } = useCurrentUser();
  return useQuery(api.friends.suggestions, user ? {} : 'skip');
}

/** The people the caller has blocked: the one place a block is lifted. */
export function useBlockedList() {
  const { user } = useCurrentUser();
  return useQuery(api.blocks.list, user ? {} : 'skip');
}

/** Handle-prefix search; undefined while loading, and not run for a blank term. */
export function useFriendSearch(term: string) {
  const { user } = useCurrentUser();
  const handle = term.trim().replace(/^@/, '');
  return useQuery(api.friends.search, user && handle ? { handle } : 'skip');
}

/** Ask, accept, decline, unfriend, dismiss a suggestion, block and unblock.
 * The reads above update reactively. */
export function useFriendActions() {
  const request = useMutation(api.friends.request);
  const accept = useMutation(api.friends.accept);
  const decline = useMutation(api.friends.decline);
  const remove = useMutation(api.friends.remove);
  const dismiss = useMutation(api.friends.dismissSuggestion);
  const block = useMutation(api.blocks.block);
  const unblock = useMutation(api.blocks.unblock);
  return {
    request: (toUserId: Id<'users'>) => request({ toUserId }),
    accept: (requestId: Id<'friendRequests'>) => accept({ requestId }),
    decline: (requestId: Id<'friendRequests'>) => decline({ requestId }),
    remove: (userId: Id<'users'>) => remove({ userId }),
    dismiss: (userId: Id<'users'>) => dismiss({ userId }),
    block: (userId: Id<'users'>) => block({ userId }),
    unblock: (userId: Id<'users'>) => unblock({ userId }),
  };
}
